import {Button, Icon, Switch, Text, TextInput} from '@gravity-ui/uikit';
import block from 'bem-cn-lite';
import {TrashBin} from '@gravity-ui/icons';

import './OrdersFilters.scss';
import {FiltersModel, ISelectOption} from '@features/orders/interfaces';
import React, {Dispatch, SetStateAction, useContext, useEffect, useState} from 'react';
import {formatCurrencyRub} from '@utils';
import {AppContext} from 'app/context';
import {
    presetsForDatePicker,
    SharedRangeDatePicker,
    SharedRangeDatePickerValue,
    SharedSelect
} from '@frontend/shared';
import {DateTime, dateTimeParse} from '@gravity-ui/date-utils';
import {RangeValue} from '@gravity-ui/date-components';
import {getFilterSuggestions} from '@features/orders/services';

const b = block('orders-filters');

interface OrdersFiltersProps {
    filters: FiltersModel;
    setFilters: Dispatch<SetStateAction<FiltersModel>>;
}

export const OrdersFilters: React.FC<OrdersFiltersProps> = ({filters, setFilters}) => {
    const {state, emit} = useContext(AppContext);
    const [isSaved, setIsSaved] = useState(false);

    function handleApplyStatusFilter(values: string[]) {
        setFilters((prev) => {
            return {
                ...prev,
                statuses: prev.statuses.map((x) => {
                    return {
                        ...x,
                        isSelected: values.includes(x.value)
                    };
                })
            };
        });
    }

    function handleApplyBrandsFilter(values: string[]) {
        setFilters((prev) => {
            return {
                ...prev,
                brands: prev.brands.map((x) => {
                    return {
                        ...x,
                        isSelected: values.includes(x.value)
                    };
                })
            };
        });
    }

    function handleRefsChange(values: string[]) {
        setFilters((prev) => {
            return {
                ...prev,
                refs: prev.refs.map((x) => {
                    return {
                        ...x,
                        isSelected: values.includes(x.value)
                    };
                })
            };
        });
    }

    function handleArticlesChange(values: string[]) {
        setFilters((prev) => {
            return {
                ...prev,
                articles: prev.articles.map((x) => {
                    return {
                        ...x,
                        isSelected: values.includes(x.value)
                    };
                })
            };
        });
    }

    function handlePriceFromChange(value: string) {
        setFilters((prev) => {
            return {
                ...prev,
                price: {
                    ...prev.price,
                    from: getNumberFromStringValue(value)
                }
            };
        });
    }

    function handlePriceToChange(value: string) {
        setFilters((prev) => {
            return {
                ...prev,
                price: {
                    ...prev.price,
                    to: getNumberFromStringValue(value)
                }
            };
        });
    }

    function handleSumFromChange(value: string) {
        setFilters((prev) => {
            return {
                ...prev,
                sum: {
                    ...prev.sum,
                    from: getNumberFromStringValue(value)
                }
            };
        });
    }

    function handleSumToChange(value: string) {
        setFilters((prev) => {
            return {
                ...prev,
                sum: {
                    ...prev.sum,
                    to: getNumberFromStringValue(value)
                }
            };
        });
    }
    function handleOnlyWithCommentsChange(checked: boolean) {
        setFilters((prev) => {
            return {
                ...prev,
                onlyWithComments: checked
            };
        });
    }

    function resetFiltersState(filters: FiltersModel): FiltersModel {
        const filterKeys = ['statuses', 'brands', 'refs', 'articles'] as const;

        const resetSelection = <T extends {isSelected: boolean}>(items: T[]): T[] => {
            return items.map((item) => ({
                ...item,
                isSelected: false
            }));
        };

        const resetFilters: FiltersModel = {
            ...filters,
            createdAtRange: null,
            deliveryDateRange: null,
            price: {},
            sum: {},
            onlyWithComments: false
        };

        filterKeys.forEach((key) => {
            if (filters[key]) {
                resetFilters[key] = resetSelection(filters[key]);
            }
        });

        return resetFilters;
    }

    function handleResetAllClick() {
        const resetFilters = resetFiltersState(filters);
        setFilters(resetFilters);

        emit('updateOrders', {
            orders: {
                filters: resetFilters
            }
        });
        setIsSaved(false);
    }

    const saveFilters = () => {
        emit('updateOrders', {
            orders: {
                filters
            }
        });
        setIsSaved(true);
    };

    // TODO Вынести в общие utils (+RangeGroup component)
    const formattedNumber = new Intl.NumberFormat('ru-RU');
    const getPlaceholderText = (value: number | undefined, isCurrency?: boolean): string => {
        if (value === null || value === undefined) return '';
        if (isCurrency) return formatCurrencyRub(value);
        return formattedNumber.format(value);
    };

    function getNumberFromStringValue(stringValue: string | undefined) {
        if (stringValue === null || stringValue === undefined) return undefined;
        let value: number | undefined = parseInt(stringValue.replace(/\s+/g, ''), 10);
        if (Number.isNaN(value)) value = undefined;
        return value;
    }

    // Проверяем, изменены ли фильтры по сравнению с сохраненными
    const isFiltersModified = () => {
        const savedFilters = state.orders.filters;
        return JSON.stringify(filters) !== JSON.stringify(savedFilters);
    };

    useEffect(() => {
        if (isFiltersModified()) {
            setIsSaved(false);
        }
    }, [filters, state.orders.filters]);

    function handleStatusFilterSuggestionsLoaded(suggestions: ISelectOption[]) {
        setFilters((prev) => {
            return {
                ...prev,
                statuses: mergeFiltersWithSuggestions(prev.statuses, suggestions)
            };
        });
    }

    function handleBrandFilterSuggestionsLoaded(suggestions: ISelectOption[]) {
        setFilters((prev) => {
            return {
                ...prev,
                brands: mergeFiltersWithSuggestions(prev.brands, suggestions)
            };
        });
    }

    function handleArticleFilterSuggestionsLoaded(suggestions: ISelectOption[]) {
        setFilters((prev) => {
            return {
                ...prev,
                articles: mergeFiltersWithSuggestions(prev.articles, suggestions)
            };
        });
    }

    function handleRefFilterSuggestionsLoaded(suggestions: ISelectOption[]) {
        setFilters((prev) => {
            return {
                ...prev,
                refs: mergeFiltersWithSuggestions(prev.refs, suggestions)
            };
        });
    }

    function mergeFiltersWithSuggestions(currentFilters: ISelectOption[], suggestions: ISelectOption[]) {
         return Array.from([...suggestions, ...currentFilters]
            .reduce((m, o) => m.set(o.label, o), new Map)
            .values());
    }

    /* TODO Вынести в utils */
    function parseDateTime(value: any): DateTime | null {
        const parsed = dateTimeParse(value);
        return parsed?.isValid ? parsed : null;
    }

    const presetsCreatedAtRange = [
        presetsForDatePicker.today,
        presetsForDatePicker.currentWeek,
        presetsForDatePicker.currentMonth,
        presetsForDatePicker.lastWeek,
        presetsForDatePicker.lastMonth
    ];

    const presetsDeliveryDateRange = [
        presetsForDatePicker.today,
        presetsForDatePicker.currentWeek,
        presetsForDatePicker.currentMonth,
        presetsForDatePicker.nextWeek,
        presetsForDatePicker.nextMonth,
        presetsForDatePicker.lastMonth
    ];
    /* ---------------- */

    const updateCreatedAtRange = (newRange: SharedRangeDatePickerValue) => {
        setFilters((prev) => ({
            ...prev,
            createdAtRange:
                newRange.start || newRange.end
                    ? ({start: newRange.start, end: newRange.end} as RangeValue<DateTime>)
                    : null
        }));
    };

    const updateDeliveryDateRange = (newRange: SharedRangeDatePickerValue) => {
        setFilters((prev) => ({
            ...prev,
            deliveryDateRange:
                newRange.start || newRange.end
                    ? ({start: newRange.start, end: newRange.end} as RangeValue<DateTime>)
                    : null
        }));
    };

    const getLabel = (filters: any, options: ISelectOption[], selectedValues: string[]): string | undefined => {
        if (selectedValues.length > 1) {
            if (options === filters.statuses) return 'Статусы:';
            if (options === filters.brands) return 'Бренды:';
            if (options === filters.articles) return 'Артикулы:';
        }
        return undefined;
    };

    return (
        <div className={b()}>
            <div className={b('filters-container')}>
                <SharedRangeDatePicker
                    className={b('date-order')}
                    value={{
                        start: filters.createdAtRange?.start
                            ? parseDateTime(filters.createdAtRange.start)
                            : null,
                        end: filters.createdAtRange?.end
                            ? parseDateTime(filters.createdAtRange.end)
                            : null
                    }}
                    onChange={updateCreatedAtRange}
                    placeholder="Дата заказа"
                    presets={presetsCreatedAtRange}
                />

                <SharedRangeDatePicker
                    className={b('date-delivery')}
                    value={{
                        start: filters.deliveryDateRange?.start
                            ? parseDateTime(filters.deliveryDateRange.start)
                            : null,
                        end: filters.deliveryDateRange?.end
                            ? parseDateTime(filters.deliveryDateRange.end)
                            : null
                    }}
                    onChange={updateDeliveryDateRange}
                    placeholder="Дата доставки"
                    presets={presetsDeliveryDateRange}
                />

                <SharedSelect
                    placeholder="Статус"
                    size="m"
                    hasClear
                    multiple
                    options={filters.statuses}
                    className={b('status')}
                    onApply={handleApplyStatusFilter}
                    onCancel={() => console.log('Отмена')}
                    onReset={() => handleApplyStatusFilter([])}
                    isStatusSelect={true}
                    filters={filters}
                    hasCounter
                    filterType={'status'}
                    onFilterSuggestionsLoaded={handleStatusFilterSuggestionsLoaded}
                    fetchSuggestions={getFilterSuggestions}
                    getLabel={getLabel}
                />

                <SharedSelect
                    placeholder="Реф №"
                    size="m"
                    hasClear
                    filterable
                    options={filters.refs}
                    onApply={handleRefsChange}
                    className={b('ref')}
                    onCancel={() => console.log('Отмена')}
                    onReset={() => handleRefsChange([])}
                    filters={filters}
                    filterType={'ref_id'}
                    onFilterSuggestionsLoaded={handleRefFilterSuggestionsLoaded}
                    fetchSuggestions={getFilterSuggestions}
                />

                <SharedSelect
                    placeholder="Бренд"
                    size="m"
                    hasClear
                    filterable
                    multiple
                    options={filters.brands}
                    className={b('brand')}
                    onApply={handleApplyBrandsFilter}
                    onCancel={() => console.log('Отмена')}
                    onReset={() => handleApplyBrandsFilter([])}
                    filters={filters}
                    hasCounter
                    filterType={'brand_name'}
                    onFilterSuggestionsLoaded={handleBrandFilterSuggestionsLoaded}
                    fetchSuggestions={getFilterSuggestions}
                    getLabel={getLabel}
                />

                <SharedSelect
                    placeholder="Артикул"
                    size="m"
                    hasClear
                    multiple
                    filterable
                    options={filters.articles}
                    className={b('article')}
                    onApply={handleArticlesChange}
                    onReset={() => handleArticlesChange([])}
                    onCancel={() => console.log('Отмена')}
                    filters={filters}
                    hasCounter
                    filterType={'article'}
                    onFilterSuggestionsLoaded={handleArticleFilterSuggestionsLoaded}
                    fetchSuggestions={getFilterSuggestions}
                    getLabel={getLabel}
                />

                <div className={b('price-block')}>
                    <TextInput
                        label="Цена:"
                        placeholder={'от\xa0' + getPlaceholderText(filters.price?.min, true)}
                        value={
                            filters.price?.from !== undefined
                                ? formattedNumber.format(filters.price.from)
                                : ''
                        }
                        onUpdate={handlePriceFromChange}
                    />
                    <TextInput
                        placeholder={'до\xa0' + getPlaceholderText(filters.price?.max, true)}
                        value={
                            filters.price?.to !== undefined
                                ? formattedNumber.format(filters.price.to)
                                : ''
                        }
                        onUpdate={handlePriceToChange}
                    />
                </div>

                <div className={b('sum-block')}>
                    <TextInput
                        label="Сумма:"
                        placeholder={'от\xa0' + getPlaceholderText(filters.sum?.min, true)}
                        value={
                            filters.sum?.from !== undefined
                                ? formattedNumber.format(filters.sum.from)
                                : ''
                        }
                        onUpdate={handleSumFromChange}
                    />
                    <TextInput
                        placeholder={'до\xa0' + getPlaceholderText(filters.sum?.max, true)}
                        value={
                            filters.sum?.to !== undefined
                                ? formattedNumber.format(filters.sum.to)
                                : ''
                        }
                        onUpdate={handleSumToChange}
                    />
                </div>

                <div className={b('comment')}>
                    <Text variant={'subheader-1'} color={'secondary'}>
                        Комментарий
                    </Text>
                    <Switch
                        size="m"
                        checked={filters.onlyWithComments}
                        onUpdate={handleOnlyWithCommentsChange}
                    ></Switch>
                </div>

                <div></div>
            </div>

            <div className={b('actions-container')}>
                <Button
                    size="m"
                    className={`${b('button-save')} ${isSaved ? `${b('button-save')}--saved` : ''}`}
                    onClick={saveFilters}
                >
                    {isSaved ? 'Сохранено' : 'Сохранить'}
                </Button>

                <Button size="m" onClick={handleResetAllClick}>
                    <Icon data={TrashBin} />

                    <Text>Сбросить все</Text>
                </Button>
            </div>
        </div>
    );
};
